// Expense receipt intake (Accounting cockpit, 2026-06-18).
//
// Mirrors ReceiptIntakeModal exactly: the admin photographs a receipt / bill →
// Gemini Flash extracts {amount, date, vendor, category, kind} → a confirmation
// screen lets the admin review/edit EVERY field → ONLY on "Valider la dépense"
// does anything get written. The photo itself is never stored (extract → confirm
// → commit the expense data only). Data-URL only = SSRF-safe.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { accountingApi, type ExpenseKind } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { ADM_COLORS as COLORS } from '@/lib/brand';
import { useT } from '@/lib/i18n';

const inputCls =
  'w-full px-2.5 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white';
const inputStyle = { borderColor: COLORS.borderSubtle } as const;

const CATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'rent', label: 'Loyer' },
  { value: 'electricity', label: 'Électricité' },
  { value: 'wifi', label: 'Internet / Wifi' },
  { value: 'salaries', label: 'Salaires' },
  { value: 'software', label: 'Logiciels' },
  { value: 'machine', label: 'Machine / Matériel' },
  { value: 'ads', label: 'Publicité' },
  { value: 'other', label: 'Autre' },
];

const KINDS: Array<{ value: ExpenseKind; label: string; hint: string }> = [
  { value: 'recurring', label: 'Récurrente', hint: 'Mensuelle fixe (loyer, salaires…)' },
  { value: 'variable', label: 'Variable', hint: 'Ponctuelle variable (pub, achats…)' },
  { value: 'one_time', label: 'Ponctuelle', hint: 'Investissement (hors P&L mensuel)' },
];

// Downscale a camera photo client-side before upload — identical to the stock
// receipt flow: longest edge ≤ 1600px, JPEG @0.8, a few hundred KB.
async function downscaleToDataUrl(file: File): Promise<string> {
  const MAX_EDGE = 1600;
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("Impossible de lire l'image, réessayez."));
      im.src = objectUrl;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error("Impossible de lire l'image, réessayez.");
    const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("Impossible de lire l'image, réessayez.");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function Shell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const { t } = useT();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto rounded-2xl p-5 shadow-2xl adm-rise"
        style={{ background: COLORS.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 mb-4">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLORS.textPrimary }}>
            📷 {t('Scanner un reçu de dépense')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Fermer')}
            className="ml-auto w-8 h-8 rounded-lg border flex items-center justify-center"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ExpenseReceiptModal({ onClose, onCommitted }: { onClose: () => void; onCommitted?: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<'pick' | 'review'>('pick');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [vendor, setVendor] = useState('');
  const [category, setCategory] = useState('other');
  const [kind, setKind] = useState<ExpenseKind>('variable');

  const extract = useMutation({
    mutationFn: async (file: File) => {
      const dataUrl = await downscaleToDataUrl(file);
      return accountingApi.receiptExtract(dataUrl);
    },
    onSuccess: ({ receipt }) => {
      setAmount(receipt.amount != null ? String(receipt.amount) : '');
      setDate(receipt.date ?? '');
      setVendor(receipt.vendor ?? '');
      setCategory(receipt.category || 'other');
      setKind(receipt.kind_guess || 'variable');
      setStage('review');
    },
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) extract.mutate(file);
  };

  const commit = useMutation({
    mutationFn: () =>
      accountingApi.receiptCommit({
        kind,
        category,
        vendor: vendor || undefined,
        amount: Number(amount),
        expense_date: date,
        notes: 'Saisie reçu',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounting-summary'] });
      queryClient.invalidateQueries({ queryKey: ['accounting-expenses'] });
      onCommitted?.();
      onClose();
    },
  });

  const canCommit = Number(amount) >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(date) && !!category;

  return (
    <Shell onClose={onClose}>
      {stage === 'pick' && (
        <div className="space-y-4">
          <p className="text-xs" style={{ color: COLORS.textSecondary }}>
            {t('Prenez en photo le reçu / la facture de dépense. L’IA lit le montant et la catégorie, puis')}
            <b> {t('vous validez')}</b> {t('avant d’enregistrer — rien n’est enregistré automatiquement.')}
          </p>
          <label
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed cursor-pointer py-10 px-4 text-center"
            style={{ borderColor: 'rgba(201,160,76,.4)', background: 'rgba(201,160,76,.05)' }}
          >
            <span className="text-3xl">📷</span>
            <span className="text-sm font-semibold" style={{ color: COLORS.accentHover }}>
              {extract.isPending ? t('Lecture du reçu en cours…') : t('Prendre une photo / choisir une image')}
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={extract.isPending}
              onChange={onFile}
            />
          </label>
          {extract.isPending && (
            <p className="text-xs text-center" style={{ color: COLORS.textTertiary }}>
              {t('L’IA analyse l’image…')}
            </p>
          )}
          {extract.isError && (
            <p className="text-xs text-center" style={{ color: COLORS.error }}>
              {(extract.error as Error).message}
            </p>
          )}
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t('Annuler')}
            </Button>
          </div>
        </div>
      )}

      {stage === 'review' && (
        <div className="space-y-3">
          <p className="text-[11px]" style={{ color: COLORS.textTertiary }}>
            {t('Vérifiez la dépense extraite, puis validez.')}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>
                {t('Montant (MAD)')}
              </label>
              <input
                className={inputCls}
                style={inputStyle}
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>
                {t('Date')}
              </label>
              <input
                className={inputCls}
                style={inputStyle}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>
              {t('Fournisseur')}
            </label>
            <input
              className={inputCls}
              style={inputStyle}
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder={t('Nom du fournisseur')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>
                {t('Catégorie')}
              </label>
              <select className={inputCls} style={inputStyle} value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {t(c.label)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>
                {t('Type')}
              </label>
              <select
                className={inputCls}
                style={inputStyle}
                value={kind}
                onChange={(e) => setKind(e.target.value as ExpenseKind)}
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {t(k.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-[11px]" style={{ color: COLORS.textTertiary }}>
            {(() => { const h = KINDS.find((k) => k.value === kind)?.hint; return h ? t(h) : ''; })()}
          </p>

          <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: COLORS.borderSubtle }}>
            <button
              type="button"
              className="text-xs underline"
              style={{ color: COLORS.textSecondary }}
              onClick={() => setStage('pick')}
            >
              {t('← Reprendre une photo')}
            </button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>
                {t('Annuler')}
              </Button>
              <Button size="sm" onClick={() => commit.mutate()} loading={commit.isPending} disabled={!canCommit}>
                {t('Valider la dépense')}
              </Button>
            </div>
          </div>
          {commit.isError && (
            <p className="text-xs" style={{ color: COLORS.error }}>
              {(commit.error as Error).message}
            </p>
          )}
        </div>
      )}
    </Shell>
  );
}

export default ExpenseReceiptModal;
